import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  RefreshCw, Plus, AlertCircle, Zap, X, ExternalLink, Bell, Play,
  ChevronRight, ChevronsDownUp, ChevronsUpDown,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import type {
  MonitoringCustomerHealth, MonitoringHealthStatus,
  MonitoringStats, MonitoringRun, MonitoringInventoryCard,
  MonitoringAlertRule,
} from '@/types/api'
import { Stat } from '@/components/primitives/Stat'
import { StatusBadge } from '@/components/primitives/StatusBadge'
import { Drawer } from '@/components/primitives/Drawer'
import PipelineScheduleTab from './PipelineScheduleTab'

// ─── Health status helpers ────────────────────────────────────────────────────

const HEALTH_DOT: Record<MonitoringHealthStatus, string> = {
  critical: 'dot-err',
  degraded: 'dot-warn',
  healthy:  'dot-ok',
  idle:     'dot-muted',
}

const HEALTH_LABEL: Record<MonitoringHealthStatus, string> = {
  critical: 'Critical',
  degraded: 'Degraded',
  healthy:  'Healthy',
  idle:     'No data',
}

// ─── Relative time ────────────────────────────────────────────────────────────

function relTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60_000)
  if (m < 1)   return 'just now'
  if (m < 60)  return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24)  return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 30)  return `${d}d ago`
  const mo = Math.floor(d / 30)
  return `${mo}mo ago`
}

// ─── KV pair ─────────────────────────────────────────────────────────────────

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 13.5 }}>{value}</div>
    </div>
  )
}

// ─── Run detail drawer ────────────────────────────────────────────────────────

function RunDetail({ run, onClose }: { run: MonitoringRun; onClose: () => void }) {
  return (
    <>
      <div className="drawer-head">
        <div>
          <div className="row" style={{ marginBottom: 6 }}>
            <StatusBadge status={run.statusNorm} />
            <span className="mono muted" style={{ fontSize: 12 }}>{run.id}</span>
          </div>
          <div className="mono" style={{ fontSize: 15, fontWeight: 600 }}>{run.item_name ?? '—'}</div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
            {run.customer_name ?? run.customer_id ?? '—'} · {run.workspace_name}
          </div>
        </div>
        <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={16} /></button>
      </div>

      <div className="drawer-body">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <KV label="Type"      value={run.execution_type ?? '—'} />
          <KV label="Event"     value={run.event_type ?? '—'} />
          <KV label="Timestamp" value={<span className="mono">{run.event_timestamp}</span>} />
          <KV label="Duration"  value={<span className="mono tabular">{run.durationFormatted}</span>} />
        </div>

        {run.error_message && (
          <div style={{
            background: 'var(--ns-err-soft)',
            border: '1px solid oklch(from var(--ns-err) l c h / 0.2)',
            borderRadius: 8, padding: 12, marginBottom: 16,
          }}>
            <div className="row" style={{ marginBottom: 6 }}>
              <AlertCircle size={14} style={{ color: 'var(--ns-err)' }} />
              <span style={{ fontWeight: 600, color: 'var(--ns-err)', fontSize: 12.5 }}>Failure reason</span>
            </div>
            <div className="mono" style={{ fontSize: 12.5 }}>{run.error_message}</div>
          </div>
        )}
      </div>

      <div className="drawer-foot">
        <button className="btn"><ExternalLink size={13} />Open in Fabric</button>
        <button className="btn"><Bell size={13} />Mute 1h</button>
        <button className="btn btn-primary"><Play size={13} />Re-run</button>
      </div>
    </>
  )
}

// ─── Health tab ───────────────────────────────────────────────────────────────

function HealthTab({
  rows, isLoading, onDrill,
}: {
  rows: MonitoringCustomerHealth[]
  isLoading: boolean
  onDrill: (tenantId: string, name: string) => void
}) {
  if (isLoading) {
    return <div className="muted" style={{ textAlign: 'center', padding: 40, fontSize: 13 }}>Loading…</div>
  }

  const critical = rows.filter(r => r.healthStatus === 'critical').length
  const degraded = rows.filter(r => r.healthStatus === 'degraded').length

  return (
    <>
      {(critical > 0 || degraded > 0) && (
        <div className="row" style={{ gap: 8, marginBottom: 16 }}>
          {critical > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 10px', borderRadius: 6, fontSize: 12.5, fontWeight: 600,
              background: 'var(--ns-err-soft)', color: 'var(--ns-err)',
              border: '1px solid oklch(from var(--ns-err) l c h / 0.25)',
            }}>
              <AlertCircle size={13} />
              {critical} customer{critical > 1 ? 's' : ''} critical
            </span>
          )}
          {degraded > 0 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '5px 10px', borderRadius: 6, fontSize: 12.5, fontWeight: 600,
              background: 'var(--ns-warn-soft)', color: 'oklch(from var(--ns-warn) calc(l - 0.1) c h)',
              border: '1px solid oklch(from var(--ns-warn) l c h / 0.25)',
            }}>
              <AlertCircle size={13} />
              {degraded} customer{degraded > 1 ? 's' : ''} degraded
            </span>
          )}
        </div>
      )}

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 16 }}></th>
              <th>Customer</th>
              <th style={{ width: 95, textAlign: 'center' }}>Workspaces</th>
              <th style={{ width: 80, textAlign: 'center' }}>Items</th>
              <th style={{ width: 90, textAlign: 'right' }}>Runs (24h)</th>
              <th style={{ width: 80, textAlign: 'right' }}>Failed</th>
              <th style={{ width: 80, textAlign: 'right' }}>Running</th>
              <th style={{ width: 100, textAlign: 'right' }}>Success rate</th>
              <th style={{ width: 120 }}>Last run</th>
              <th style={{ width: 32 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr
                key={r.tenant_id}
                className="row-clickable"
                onClick={() => onDrill(r.tenant_id, r.customer_name ?? r.tenant_id)}
              >
                <td>
                  <span className={`dot ${HEALTH_DOT[r.healthStatus]}`} style={{ display: 'block' }} title={HEALTH_LABEL[r.healthStatus]} />
                </td>
                <td>
                  <div style={{ fontWeight: 500 }}>{r.customer_name ?? r.tenant_id}</div>
                  {r.onboarding_status && r.onboarding_status !== 'complete' && (
                    <div className="muted" style={{ fontSize: 11.5 }}>{r.onboarding_status}</div>
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {r.workspace_count > 0
                    ? <span className="badge badge-brand mono">{r.workspace_count}</span>
                    : <span className="muted">—</span>}
                </td>
                <td className="tabular muted" style={{ textAlign: 'center', fontSize: 13 }}>
                  {r.item_count > 0 ? r.item_count : '—'}
                </td>
                <td className="tabular" style={{ textAlign: 'right', fontSize: 13 }}>
                  {r.runs_24h > 0 ? r.runs_24h : <span className="muted">—</span>}
                </td>
                <td className="tabular" style={{ textAlign: 'right', fontSize: 13 }}>
                  {r.failed_24h > 0
                    ? <span style={{ color: 'var(--ns-err)', fontWeight: 600 }}>{r.failed_24h}</span>
                    : <span className="muted">—</span>}
                </td>
                <td className="tabular" style={{ textAlign: 'right', fontSize: 13 }}>
                  {r.running_now > 0
                    ? <span style={{ color: 'var(--ns-info)', fontWeight: 600 }}>{r.running_now}</span>
                    : <span className="muted">—</span>}
                </td>
                <td className="tabular" style={{ textAlign: 'right', fontSize: 13 }}>
                  {r.successRate != null
                    ? <span style={{ color: r.successRate < 80 ? 'var(--ns-err)' : r.successRate < 95 ? 'oklch(from var(--ns-warn) calc(l - 0.1) c h)' : 'var(--ns-ok)', fontWeight: 500 }}>
                        {r.successRate}%
                      </span>
                    : <span className="muted">—</span>}
                </td>
                <td className="muted" style={{ fontSize: 12 }} title={r.last_run_at ?? undefined}>
                  {relTime(r.last_run_at)}
                </td>
                <td>
                  <ChevronRight size={13} style={{ color: 'var(--ns-text-subtle)' }} />
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={10} className="muted" style={{ textAlign: 'center', padding: 40, fontSize: 13 }}>
                  No customers in the monitoring database.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ─── Runs tab ─────────────────────────────────────────────────────────────────

function RunsTab({
  runs, isLoading, statusFilter, onStatusFilter,
  timeRange, onTimeRange, drillName, onClearDrill,
  onSelectRun,
}: {
  runs: MonitoringRun[]
  isLoading: boolean
  statusFilter: string
  onStatusFilter: (s: string) => void
  timeRange: 24 | 168 | 720
  onTimeRange: (h: 24 | 168 | 720) => void
  drillName: string | null
  onClearDrill: () => void
  onSelectRun: (r: MonitoringRun) => void
}) {
  const toggle = (s: string) => onStatusFilter(statusFilter === s ? 'all' : s)

  return (
    <>
      <div className="toolbar">
        {drillName && (
          <button className="chip active" onClick={onClearDrill}>
            {drillName} <X size={11} style={{ marginLeft: 2 }} />
          </button>
        )}
        <button className={`chip ${statusFilter === 'failed' ? 'active' : ''}`} onClick={() => toggle('failed')}>
          <span className="dot dot-err" />Failed
        </button>
        <button className={`chip ${statusFilter === 'running' ? 'active' : ''}`} onClick={() => toggle('running')}>
          <span className="dot" style={{ background: 'var(--ns-info)' }} />Running
        </button>
        <div className="spacer" />
        <div className="seg">
          {([24, 168, 720] as const).map(h => (
            <button key={h} className={timeRange === h ? 'active' : ''} onClick={() => onTimeRange(h)}>
              {h === 24 ? '24h' : h === 168 ? '7d' : '30d'}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="muted" style={{ textAlign: 'center', padding: 40, fontSize: 13 }}>Loading…</div>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Status</th>
                <th>Item</th>
                <th>Customer</th>
                <th>Workspace</th>
                <th style={{ width: 90 }}>Type</th>
                <th style={{ width: 110 }}>Event</th>
                <th style={{ width: 110 }}>When</th>
                <th style={{ width: 100, textAlign: 'right' }}>Duration</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id} className="row-clickable" onClick={() => onSelectRun(r)}>
                  <td><StatusBadge status={r.statusNorm} /></td>
                  <td>
                    <div className="mono" style={{ fontWeight: 500 }}>{r.item_name ?? '—'}</div>
                    {r.error_message && (
                      <div style={{ color: 'var(--ns-err)', fontSize: 11.5, marginTop: 2 }}>{r.error_message}</div>
                    )}
                  </td>
                  <td style={{ fontSize: 13 }}>
                    {r.customer_name ?? <span className="mono muted" style={{ fontSize: 11 }}>{r.customer_id}</span>}
                  </td>
                  <td className="mono muted" style={{ fontSize: 12 }}>{r.workspace_name}</td>
                  <td><span className="badge">{r.execution_type ?? '—'}</span></td>
                  <td className="muted" style={{ fontSize: 12 }}>{r.event_type ?? '—'}</td>
                  <td className="muted" style={{ fontSize: 12 }} title={r.event_timestamp ?? undefined}>
                    {relTime(r.event_timestamp)}
                  </td>
                  <td className="mono tabular" style={{ textAlign: 'right' }}>{r.durationFormatted}</td>
                </tr>
              ))}
              {runs.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 40, fontSize: 13 }}>
                    No runs in the selected range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

// ─── Inventory tab ────────────────────────────────────────────────────────────

type InventoryHealthFilter = 'all' | 'failing' | 'warning' | 'healthy' | 'stale'

const STALE_MS = 7 * 24 * 60 * 60 * 1000

function InventoryTab({
  cards, isLoading, onDrillRuns,
}: {
  cards: MonitoringInventoryCard[]
  isLoading: boolean
  onDrillRuns: (tenantId: string, name: string) => void
}) {
  const [search,       setSearch]   = useState('')
  const [healthFilter, setHealth]   = useState<InventoryHealthFilter>('all')
  const [collapsed,    setCollapsed] = useState<Set<string>>(new Set())
  const now = Date.now()

  const isStale = (iso: string | null) =>
    iso ? (now - new Date(iso).getTime()) > STALE_MS : true

  const filtered = cards.filter(c => {
    if (search && !c.workspace_name.toLowerCase().includes(search.toLowerCase())) return false
    if (healthFilter === 'failing') return c.failing > 0
    if (healthFilter === 'warning') return c.warning > 0 && c.failing === 0
    if (healthFilter === 'healthy') return c.failing === 0 && c.warning === 0
    if (healthFilter === 'stale')   return isStale(c.last_discovered_at ?? null)
    return true
  })

  // Count for filter chips
  const failingCount = cards.filter(c => c.failing > 0).length
  const warningCount = cards.filter(c => c.warning > 0 && c.failing === 0).length
  const staleCount   = cards.filter(c => isStale(c.last_discovered_at ?? null)).length

  // Group by customer
  const grouped = new Map<string, { name: string; items: MonitoringInventoryCard[] }>()
  for (const c of filtered) {
    if (!grouped.has(c.tenant_id)) grouped.set(c.tenant_id, { name: c.customer_name ?? c.tenant_id, items: [] })
    grouped.get(c.tenant_id)!.items.push(c)
  }

  // Sort: failing customers first, then alpha
  const sortedGroups = [...grouped.entries()].sort(([, a], [, b]) => {
    const aF = a.items.reduce((s, c) => s + c.failing, 0)
    const bF = b.items.reduce((s, c) => s + c.failing, 0)
    if (bF !== aF) return bF - aF
    return a.name.localeCompare(b.name)
  })

  const allCollapsed  = sortedGroups.every(([k]) => collapsed.has(k))
  const toggleAll     = () => {
    if (allCollapsed) setCollapsed(new Set())
    else              setCollapsed(new Set(sortedGroups.map(([k]) => k)))
  }
  const toggleOne = (k: string) => setCollapsed(prev => {
    const next = new Set(prev)
    next.has(k) ? next.delete(k) : next.add(k)
    return next
  })

  // Summary totals (unfiltered)
  const totalWorkspaces = cards.length
  const totalItems      = cards.reduce((s, c) => s + c.total_items, 0)
  const totalFailing    = cards.reduce((s, c) => s + c.failing, 0)
  const totalCustomers  = new Set(cards.map(c => c.tenant_id)).size

  if (isLoading) {
    return <div className="muted" style={{ textAlign: 'center', padding: 40, fontSize: 13 }}>Loading…</div>
  }

  return (
    <>
      {/* Summary line */}
      <div className="row" style={{ gap: 16, marginBottom: 12, fontSize: 12.5, color: 'var(--ns-text-muted)' }}>
        <span><span className="tabular" style={{ fontWeight: 600, color: 'var(--ns-text)' }}>{totalCustomers}</span> customers</span>
        <span><span className="tabular" style={{ fontWeight: 600, color: 'var(--ns-text)' }}>{totalWorkspaces}</span> workspaces</span>
        <span><span className="tabular" style={{ fontWeight: 600, color: 'var(--ns-text)' }}>{totalItems}</span> items</span>
        {totalFailing > 0 && (
          <span style={{ color: 'var(--ns-err)', fontWeight: 600 }}>
            <span className="tabular">{totalFailing}</span> failing
          </span>
        )}
      </div>

      {/* Toolbar */}
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <input
          type="text"
          className="search-box"
          placeholder="Search workspaces…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 220 }}
        />
        <button className={`chip ${healthFilter === 'all' ? 'active' : ''}`} onClick={() => setHealth('all')}>All</button>
        {failingCount > 0 && (
          <button className={`chip ${healthFilter === 'failing' ? 'active' : ''}`} onClick={() => setHealth(h => h === 'failing' ? 'all' : 'failing')}>
            <span className="dot dot-err" />Failing <span className="count">{failingCount}</span>
          </button>
        )}
        {warningCount > 0 && (
          <button className={`chip ${healthFilter === 'warning' ? 'active' : ''}`} onClick={() => setHealth(h => h === 'warning' ? 'all' : 'warning')}>
            <span className="dot dot-warn" />Warning <span className="count">{warningCount}</span>
          </button>
        )}
        {staleCount > 0 && (
          <button className={`chip ${healthFilter === 'stale' ? 'active' : ''}`} onClick={() => setHealth(h => h === 'stale' ? 'all' : 'stale')}>
            Stale <span className="count">{staleCount}</span>
          </button>
        )}
        <div className="spacer" />
        <span className="muted" style={{ fontSize: 12 }}>{filtered.length} workspace{filtered.length !== 1 ? 's' : ''}</span>
        <button className="btn btn-ghost btn-sm" onClick={toggleAll} title={allCollapsed ? 'Expand all' : 'Collapse all'}>
          {allCollapsed ? <ChevronsUpDown size={13} /> : <ChevronsDownUp size={13} />}
          {allCollapsed ? 'Expand all' : 'Collapse all'}
        </button>
      </div>

      {/* Grouped workspace sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {sortedGroups.map(([tenantId, group]) => {
          const gTotal   = group.items.reduce((s, c) => s + c.total_items, 0)
          const gFailing = group.items.reduce((s, c) => s + c.failing, 0)
          const gWarning = group.items.reduce((s, c) => s + c.warning, 0)
          const gHealthy = group.items.reduce((s, c) => s + c.healthy, 0)
          const isOpen   = !collapsed.has(tenantId)

          return (
            <div key={tenantId}>
              {/* Customer header */}
              <button
                onClick={() => toggleOne(tenantId)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '8px 12px',
                  background: 'var(--ns-surface-2)',
                  border: '1px solid var(--ns-border)',
                  borderRadius: isOpen ? '8px 8px 0 0' : 8,
                  cursor: 'pointer', textAlign: 'left',
                  transition: 'border-radius 0.1s',
                }}
              >
                <ChevronRight size={13} style={{
                  transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s',
                  color: 'var(--ns-text-muted)',
                  flexShrink: 0,
                }} />
                <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>{group.name}</span>
                <span className="muted" style={{ fontSize: 12 }}>
                  {group.items.length} workspace{group.items.length !== 1 ? 's' : ''}
                </span>
                <span className="muted" style={{ fontSize: 12 }}>·</span>
                <span className="muted" style={{ fontSize: 12 }}>{gTotal} items</span>
                {gFailing > 0 && (
                  <span className="row" style={{ gap: 4, marginLeft: 8 }}>
                    <span className="dot dot-err" />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-err)' }}>{gFailing} failing</span>
                  </span>
                )}
                {gWarning > 0 && gFailing === 0 && (
                  <span className="row" style={{ gap: 4, marginLeft: 8 }}>
                    <span className="dot dot-warn" />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'oklch(from var(--ns-warn) calc(l - 0.1) c h)' }}>{gWarning} warning</span>
                  </span>
                )}
                {/* Mini aggregate health bar */}
                {gTotal > 0 && (
                  <div style={{ display: 'flex', gap: 2, height: 6, width: 56, borderRadius: 3, overflow: 'hidden', marginLeft: 8, flexShrink: 0 }}>
                    <div style={{ flex: gHealthy, background: 'var(--ns-ok)' }} />
                    {gWarning > 0 && <div style={{ flex: gWarning, background: 'var(--ns-warn)' }} />}
                    {gFailing > 0 && <div style={{ flex: gFailing, background: 'var(--ns-err)' }} />}
                  </div>
                )}
              </button>

              {/* Cards grid */}
              {isOpen && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                  gap: 8, padding: 8,
                  border: '1px solid var(--ns-border)', borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  background: 'var(--ns-surface)',
                }}>
                  {group.items.map(item => {
                    const total      = item.total_items || 1
                    const allHealthy = item.failing === 0 && item.warning === 0
                    const stale      = isStale(item.last_discovered_at ?? null)

                    return (
                      <div
                        key={`${item.tenant_id}-${item.workspace_name}`}
                        className="card"
                        style={{ padding: 12, cursor: 'pointer', margin: 0 }}
                        onClick={() => onDrillRuns(item.tenant_id, group.name)}
                        title="View runs for this customer"
                      >
                        {/* Workspace name + badges */}
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6, marginBottom: 8 }}>
                          <div className="mono" style={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.3, wordBreak: 'break-all' }}>
                            {item.workspace_name}
                          </div>
                          <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                            {item.capacity_sku && (
                              <span className="badge badge-brand mono" style={{ fontSize: 10 }}>{item.capacity_sku}</span>
                            )}
                            {stale && (
                              <span
                                className="badge"
                                style={{ fontSize: 10, color: 'oklch(from var(--ns-warn) calc(l - 0.15) c h)', borderColor: 'var(--ns-warn)' }}
                                title="Not discovered in over 7 days"
                              >stale</span>
                            )}
                          </div>
                        </div>

                        {/* Health bar — thin, solid green when all healthy */}
                        <div style={{ display: 'flex', gap: 2, height: 4, borderRadius: 2, overflow: 'hidden', marginBottom: 8 }}>
                          {allHealthy
                            ? <div style={{ flex: 1, background: 'var(--ns-ok)' }} />
                            : <>
                                <div style={{ flex: item.healthy / total, background: 'var(--ns-ok)' }} />
                                {item.warning > 0 && <div style={{ flex: item.warning / total, background: 'var(--ns-warn)' }} />}
                                {item.failing > 0 && <div style={{ flex: item.failing / total, background: 'var(--ns-err)' }} />}
                              </>
                          }
                        </div>

                        {/* Item counts — only show dots when not fully healthy */}
                        <div className="row" style={{ justifyContent: 'space-between', fontSize: 11.5 }}>
                          <span>
                            <span className="tabular" style={{ fontWeight: 600 }}>{item.total_items}</span>
                            {' '}<span className="muted">items</span>
                          </span>
                          {!allHealthy && (
                            <div className="row" style={{ gap: 8 }}>
                              {item.failing > 0 && (
                                <span className="row" style={{ gap: 3 }}>
                                  <span className="dot dot-err" />
                                  <span className="tabular" style={{ color: 'var(--ns-err)', fontWeight: 600 }}>{item.failing}</span>
                                </span>
                              )}
                              {item.warning > 0 && (
                                <span className="row" style={{ gap: 3 }}>
                                  <span className="dot dot-warn" />
                                  <span className="tabular">{item.warning}</span>
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="divider" style={{ margin: '8px 0 6px' }} />
                        <div className="row" style={{ justifyContent: 'space-between', fontSize: 11 }}>
                          <span className="muted">Last discovered</span>
                          <span
                            className="muted tabular"
                            style={stale ? { color: 'oklch(from var(--ns-warn) calc(l - 0.1) c h)' } : {}}
                            title={item.last_discovered_at ?? undefined}
                          >
                            {relTime(item.last_discovered_at ?? null)}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}

        {sortedGroups.length === 0 && (
          <div className="muted" style={{ fontSize: 13, padding: 40, textAlign: 'center' }}>
            No workspaces match the current filter.
          </div>
        )}
      </div>
    </>
  )
}

// ─── Alert rules tab ──────────────────────────────────────────────────────────

function AlertsTab({ rules, isLoading }: { rules: MonitoringAlertRule[]; isLoading: boolean }) {
  const queryClient = useQueryClient()

  const toggleMutation = useMutation({
    mutationFn: (rule: MonitoringAlertRule) =>
      api.put(`/monitoring/alert-rules/${rule.id}`, { isEnabled: !rule.isEnabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['monitoring', 'alert-rules'] }),
  })

  if (isLoading) {
    return <div className="muted" style={{ textAlign: 'center', padding: 40, fontSize: 13 }}>Loading…</div>
  }

  return (
    <div className="card">
      <table className="table">
        <thead>
          <tr>
            <th>Alert type</th>
            <th>Customer</th>
            <th>Workspace pattern</th>
            <th>Channel</th>
            <th>Target</th>
            <th style={{ width: 60 }}></th>
          </tr>
        </thead>
        <tbody>
          {rules.map(a => (
            <tr key={a.id} style={{ opacity: a.isEnabled ? 1 : 0.55 }}>
              <td>
                <div style={{ fontWeight: 500 }}>{a.alert_type ?? '—'}</div>
                <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>#{a.id}</div>
              </td>
              <td className="muted" style={{ fontSize: 12.5 }}>
                {a.customer_name ?? <span style={{ fontStyle: 'italic' }}>All customers</span>}
              </td>
              <td className="mono" style={{ fontSize: 12 }}>{a.workspace_pattern ?? '*'}</td>
              <td><span className="badge">{a.alert_channel ?? '—'}</span></td>
              <td className="mono muted" style={{ fontSize: 11.5, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {a.channel_target ?? '—'}
              </td>
              <td>
                <button
                  className="btn btn-ghost"
                  style={{ padding: '2px 6px' }}
                  disabled={toggleMutation.isPending}
                  onClick={() => toggleMutation.mutate(a)}
                >
                  <span style={{
                    display: 'inline-block', width: 30, height: 18, borderRadius: 999,
                    background: a.isEnabled ? 'var(--ns-brand-strong)' : 'var(--ns-border-strong)',
                    position: 'relative', transition: 'background 0.15s',
                  }}>
                    <span style={{
                      position: 'absolute', top: 2, left: a.isEnabled ? 14 : 2,
                      width: 14, height: 14, borderRadius: '50%', background: 'white',
                      transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    }} />
                  </span>
                </button>
              </td>
            </tr>
          ))}
          {rules.length === 0 && (
            <tr>
              <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 40, fontSize: 13 }}>
                No alert rules configured.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

type TabId = 'health' | 'runs' | 'inventory' | 'alerts' | 'schedule'

export default function MonitoringPage() {
  const queryClient = useQueryClient()

  const [tab, setTab]             = useState<TabId>('health')
  const [drillTenantId, setDrill] = useState<string | null>(null)
  const [drillName, setDrillName] = useState<string | null>(null)
  const [statusFilter, setStatus] = useState('all')
  const [timeRange, setTimeRange] = useState<24 | 168 | 720>(24)
  const [selectedRun, setRun]     = useState<MonitoringRun | null>(null)

  const drillToRuns = (tenantId: string, name: string) => {
    setDrill(tenantId)
    setDrillName(name)
    setTab('runs')
  }

  const clearDrill = () => { setDrill(null); setDrillName(null) }

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['monitoring', 'stats'],
    queryFn:  () => api.get<MonitoringStats>('/monitoring/stats'),
    refetchInterval: 60_000,
  })

  const { data: health = [], isLoading: healthLoading } = useQuery({
    queryKey: ['monitoring', 'health'],
    queryFn:  () => api.get<MonitoringCustomerHealth[]>('/monitoring/health'),
    enabled:  tab === 'health',
    refetchInterval: 30_000,
  })

  const runsParams = new URLSearchParams({ hours: String(timeRange) })
  if (statusFilter !== 'all') runsParams.set('status', statusFilter)
  if (drillTenantId)          runsParams.set('tenantId', drillTenantId)

  const { data: runs = [], isLoading: runsLoading } = useQuery({
    queryKey: ['monitoring', 'runs', statusFilter, timeRange, drillTenantId],
    queryFn:  () => api.get<MonitoringRun[]>(`/monitoring/runs?${runsParams}`),
    enabled:  tab === 'runs',
    refetchInterval: 30_000,
  })

  const { data: inventory = [], isLoading: inventoryLoading } = useQuery({
    queryKey: ['monitoring', 'inventory'],
    queryFn:  () => api.get<MonitoringInventoryCard[]>('/monitoring/inventory'),
    enabled:  tab === 'inventory',
    refetchInterval: 60_000,
  })

  const { data: alertRules = [], isLoading: alertsLoading } = useQuery({
    queryKey: ['monitoring', 'alert-rules'],
    queryFn:  () => api.get<MonitoringAlertRule[]>('/monitoring/alert-rules'),
    enabled:  tab === 'alerts',
  })

  const criticalCount = health.filter(r => r.healthStatus === 'critical').length

  return (
    <div className="page">

      {/* Page header */}
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Monitoring</div>
          <h1 className="page-title">Fabric workspace health</h1>
          <div className="page-subtitle">Customer Fabric solution monitoring · live</div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => queryClient.invalidateQueries({ queryKey: ['monitoring'] })}>
            <RefreshCw size={14} />Refresh
          </button>
          <button className="btn btn-brand"><Plus size={14} />New alert rule</button>
        </div>
      </div>

      {/* Stat strip */}
      <div className="stat-grid">
        <Stat
          label="Runs (24h)"
          value={statsLoading ? '…' : String(stats?.runs24h ?? 0)}
          sparkData={[14, 18, 22, 19, 24, 26, 22, 28, 31, 29, 27, 30]}
        />
        <Stat
          label={<span className="row" style={{ gap: 5 }}><AlertCircle size={12} style={{ color: 'var(--ns-err)' }} />Failed</span>}
          value={statsLoading ? '…' : String(stats?.failed24h ?? 0)}
          sparkData={[0, 1, 0, 0, 2, 1, 0, 1, 2, 2, 2, 2]}
        />
        <Stat
          label={<span className="row" style={{ gap: 5 }}><Zap size={12} style={{ color: 'var(--ns-info)' }} />Running</span>}
          value={statsLoading ? '…' : String(stats?.running ?? 0)}
          sparkData={[2, 1, 3, 2, 4, 3, 2, 1, 2, 1, 0, 1]}
        />
        <Stat
          label="Success rate"
          value={statsLoading ? '…' : `${stats?.successRate ?? 100}%`}
          sparkData={[98, 97, 99, 95, 96, 94, 97, 95, 92, 91, 93, 92]}
        />
        <Stat
          label="P95 runtime (24h)"
          value={statsLoading ? '…' : (stats?.p95Formatted ?? '—')}
          sparkData={[5.2, 5.4, 5.8, 6.1, 5.9, 6.4, 6.2, 6.5, 6.8, 6.6, 6.7, 6.7]}
        />
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${tab === 'health' ? 'active' : ''}`} onClick={() => setTab('health')}>
          Customer health
          {criticalCount > 0 && (
            <span className="count" style={{ background: 'var(--ns-err)', color: 'white' }}>{criticalCount}</span>
          )}
        </button>
        <button className={`tab ${tab === 'runs' ? 'active' : ''}`} onClick={() => setTab('runs')}>
          Run history
          {drillName && <span className="count">{drillName}</span>}
        </button>
        <button className={`tab ${tab === 'inventory' ? 'active' : ''}`} onClick={() => setTab('inventory')}>
          Inventory
        </button>
        <button className={`tab ${tab === 'alerts' ? 'active' : ''}`} onClick={() => setTab('alerts')}>
          Alert rules
        </button>
        <button className={`tab ${tab === 'schedule' ? 'active' : ''}`} onClick={() => setTab('schedule')}>
          Pipeline schedule
        </button>
      </div>

      {tab === 'health' && (
        <HealthTab rows={health} isLoading={healthLoading} onDrill={drillToRuns} />
      )}

      {tab === 'runs' && (
        <RunsTab
          runs={runs}
          isLoading={runsLoading}
          statusFilter={statusFilter}
          onStatusFilter={setStatus}
          timeRange={timeRange}
          onTimeRange={setTimeRange}
          drillName={drillName}
          onClearDrill={clearDrill}
          onSelectRun={setRun}
        />
      )}

      {tab === 'inventory' && (
        <InventoryTab cards={inventory} isLoading={inventoryLoading} onDrillRuns={drillToRuns} />
      )}

      {tab === 'alerts' && (
        <AlertsTab rules={alertRules} isLoading={alertsLoading} />
      )}

      {tab === 'schedule' && (
        <PipelineScheduleTab />
      )}

      <Drawer open={!!selectedRun} onClose={() => setRun(null)}>
        {selectedRun && <RunDetail run={selectedRun} onClose={() => setRun(null)} />}
      </Drawer>
    </div>
  )
}
